/**
 * useStep7GoalsClaims — Step 7 lifecycle.
 *
 * The `cybersecurity_goals_agent` emits TWO sibling arrays in one
 * result envelope:
 *   - `cybersecurity_goals`  (per ISO 21434 RQ-09-05, for Reduce risks)
 *   - `cybersecurity_claims` (per RQ-09-06, for Share/Retain risks)
 *
 * Single fetch returns both; the hook splits internally. CRUD shares
 * one `useRecordCRUD` instance (backend differentiates goal_id vs
 * claim_id from the payload).
 *
 * Hierarchical approve is **one** API call. Although the UI splits
 * goals + claims into independent sub-sections (each with its own
 * Current/Child tabs), the underlying step has one approval workflow
 * — when the user clicks the global Approve button, the combined
 * relevant-records list (goals + claims) goes to
 * `approveHierarchicalData(... 'cybersecurity_goals_agent', combined)`
 * in a single call. Per-collection modifications state still lives
 * in two separate `useState` slots so the per-tab editors can target
 * the right records, but the approve action unifies them.
 */
import { useCallback, useState } from 'react';
import { getAgentResult, approveHierarchicalData } from '../../../api/workflow';
import { notify } from '../../../services/notificationBus';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import { useRecordCRUD } from '../hooks/useRecordCRUDv2';
import type { Step7BodyProps } from '../step-components/stepBodyProps';
import type {
  HierarchicalModifications,
  PerStepState,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';
import type { OptionLite } from '../step-components/step7-goals/GoalFormModal';
import type { GoalRecord } from '../step-components/step7-goals/goalsFieldSchema';
import type { ClaimRecord } from '../step-components/step7-goals/claimsFieldSchema';

const HAS_OUTPUT = new Set(['draft', 'revising', 'approved']);
const AGENT_NAME = 'cybersecurity_goals_agent';
const RISK_AGENT_NAME = 'risk_assessment_agent';

interface AgentResultResponse {
  result?: Record<string, unknown>;
  [key: string]: unknown;
}

interface RiskLite {
  risk_id?: string;
  treatment_decision?: string;
  [key: string]: unknown;
}

interface GoalsClaimsPayload {
  goals: GoalRecord[];
  claims: ClaimRecord[];
}

export interface UseStep7Input {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  workflowCompleted: boolean;
  analysisMode?: string | null;
  /** Reducer state — Story H8 lifts the dual-collection modifications
   *  (goals + claims) into `state.steps[7].hierarchicalModifications`
   *  so they survive step navigation. Mirrors what H2 did for Steps 2-6
   *  via `useHierarchicalApprove`. */
  state: WorkflowV2State;
  actions: WorkflowActions;
}

export function useStep7GoalsClaims({
  threadId,
  stepState,
  workflowCompleted,
  analysisMode,
  state,
  actions,
}: UseStep7Input): Step7BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT.has(status);
  const editable = workflowCompleted || status === 'draft';

  /* ----- Goals + Claims fetch (one API call, two arrays) ----- */

  const fetchGoalsClaims = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractGoalsClaims = useCallback(
    (raw: AgentResultResponse | null): GoalsClaimsPayload => {
      const r = raw?.result as Record<string, unknown> | undefined;
      const goals = (r?.cybersecurity_goals ?? r?.goals) as GoalRecord[] | undefined;
      const claims = (r?.cybersecurity_claims ?? r?.claims) as ClaimRecord[] | undefined;
      return {
        goals: Array.isArray(goals) ? goals : [],
        claims: Array.isArray(claims) ? claims : [],
      };
    },
    [],
  );

  const structured = useStructuredFetch<AgentResultResponse | null, GoalsClaimsPayload>({
    fetcher: fetchGoalsClaims,
    extract: extractGoalsClaims,
    enabled: hasOutput && !!threadId,
  });
  useRefetchOnReviseComplete(status, structured.refetch);

  const goals = structured.data?.goals ?? [];
  const claims = structured.data?.claims ?? [];

  /* ----- Risk list fetch (for both modals' risk_ids multi-select) ----- */

  const fetchRisks = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, RISK_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractRisks = useCallback((raw: AgentResultResponse | null): RiskLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.risk_assessments ?? r?.risk_assessment ?? r?.risks) as RiskLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const risksFetch = useStructuredFetch<AgentResultResponse | null, RiskLite[]>({
    fetcher: fetchRisks,
    extract: extractRisks,
    enabled: editable && !!threadId,
  });

  // Dedupe by risk_id (child-TARA collisions). First label wins.
  const riskOptions: OptionLite[] = (() => {
    const seen = new Set<string>();
    const out: OptionLite[] = [];
    for (const r of risksFetch.data ?? []) {
      const id = String(r.risk_id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const label = r.treatment_decision ? `${id} — ${r.treatment_decision}` : id;
      out.push({ value: id, label });
    }
    return out;
  })();

  /* ----- CRUD (one instance, both collections route through it) ----- */

  const crud = useRecordCRUD({
    threadId,
    agentName: AGENT_NAME,
    enabled: editable,
  });

  /* ----- Unified hierarchical approve -----
   * Same agent owns both goals + claims, so a single
   * `approveHierarchicalData` call writes the merged result. Per-
   * collection modifications state stays separate (goal_ids vs
   * claim_ids never collide; the UI sub-tabs target their own
   * setters), but the approve action combines both lists +
   * modifications into one API call. */

  const hierarchicalEnabled = analysisMode === 'hierarchical';

  // Story H8 — single reducer-backed map covers BOTH goals and
  // claims modifications. `goal_id` and `claim_id` namespaces don't
  // collide, so a flat `Record<string, ...>` is safe; the dual-tab
  // child views (ChildGoalsTable + ChildClaimsTable) each look up
  // by their own primary key and ignore the rest.
  const modifications = state.steps[7]?.hierarchicalModifications ?? {};
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const setGoalRelevance = useCallback(
    (id: string, isRelevant: boolean) => actions.setHierRelevance(7, id, isRelevant),
    [actions],
  );
  const setGoalJustification = useCallback(
    (id: string, justification: string) => actions.setHierJustification(7, id, justification),
    [actions],
  );
  const setClaimRelevance = useCallback(
    (id: string, isRelevant: boolean) => actions.setHierRelevance(7, id, isRelevant),
    [actions],
  );
  const setClaimJustification = useCallback(
    (id: string, justification: string) => actions.setHierJustification(7, id, justification),
    [actions],
  );

  /** Resolve approved records for one collection — newItems (rows
   *  without a child source) plus any child rows the user marked
   *  relevant (or that were relevant by default). Same logic as
   *  `useHierarchicalApprove.onApprove` but factored out so we can
   *  call it twice and merge the results before the single API call. */
  const filterApproved = useCallback(
    (
      records: Array<Record<string, unknown>>,
      primaryKey: string,
      mods: HierarchicalModifications,
    ): Array<Record<string, unknown>> => {
      const newItems = records.filter((r) => r?.source !== 'child_tara');
      const children = records.filter((r) => r?.source === 'child_tara');
      const relevant = children.filter((c) => {
        const id = String(c[primaryKey] ?? '');
        const override = mods[id]?.is_relevant;
        if (override === undefined) return c.is_relevant !== false;
        return override !== false;
      });
      return [...newItems, ...relevant];
    },
    [],
  );

  const unifiedApprove = useCallback(async () => {
    if (!hierarchicalEnabled) return;
    if (!threadId) throw new Error('No active workflow session');
    setApproving(true);
    setApproveError(null);
    try {
      const approvedGoals = filterApproved(
        goals as Array<Record<string, unknown>>,
        'goal_id',
        modifications,
      );
      const approvedClaims = filterApproved(
        claims as Array<Record<string, unknown>>,
        'claim_id',
        modifications,
      );
      // Single API call covers both collections — backend
      // distinguishes goals vs claims by record shape (goal_id /
      // goal_statement vs claim_id / claim_statement) and writes
      // them into the agent's two sibling arrays. The merged
      // `modifications` map carries both subsets — backend's
      // `approve-hierarchical` handler ignores entries that don't
      // match any record in the agent's result, so the merge is safe.
      await approveHierarchicalData(
        threadId,
        AGENT_NAME,
        [...approvedGoals, ...approvedClaims],
        modifications,
      );
      // Story H8 — clear the reducer-backed diff map so the next
      // edit session starts fresh (mirrors `useHierarchicalApprove`).
      actions.clearHierModifications(7);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to approve relevance';
      const message = typeof msg === 'string' ? msg : 'Failed to approve relevance';
      setApproveError(message);
      // Story 15.G — bring toast parity with `useHierarchicalApprove`
      // (Step 7's dual-collection unifiedApprove can't use the shared
      // hook so the notify lives here too).
      notify({ type: 'error', title: 'Save failed', message });
      throw e;
    } finally {
      setApproving(false);
    }
  }, [hierarchicalEnabled, threadId, goals, claims, modifications, filterApproved, structured, actions]);

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    editable,
    structured,
    goals,
    claims,
    crud,
    riskOptions,
    goalsHierarchical: {
      enabled: hierarchicalEnabled,
      modifications,
      setRelevance: setGoalRelevance,
      setJustification: setGoalJustification,
      // Both sub-section `onApprove` references point at the same
      // unified approve. The global Approve button uses
      // `approveOverride` below; per-section approve buttons (if
      // ever rendered) would also fire the unified flow.
      onApprove: unifiedApprove,
      approving,
      approveError,
    },
    claimsHierarchical: {
      enabled: hierarchicalEnabled,
      modifications,
      setRelevance: setClaimRelevance,
      setJustification: setClaimJustification,
      onApprove: unifiedApprove,
      approving,
      approveError,
    },
    approveOverride: hierarchicalEnabled
      ? { onApprove: unifiedApprove, approving }
      : undefined,
  };
}
